import { useMemo } from 'react'

import { ThemeProvider } from '@mui/material/styles'

import appConfig from '~/src/App/config'
import getGlobal from '~/src/Lib/getGlobal'
import createLogger from '~/src/Lib/Logging'
import safeStorage from '~/src/Lib/safeStorage'
import { repr, shallowEqualsMemoizer } from '~/src/Lib/Utils'
import { useConnectModern } from '~/src/Store/hooks/useConnectModern'

import { palettes } from './colors'
import { buildTheme } from './mui'

const logger = createLogger('UI/Theme')

const IGNORED = new Set(['$$typeof', Symbol.toStringTag])

export { palettes }

const themes = new Proxy(
  {
    DEFAULT: buildTheme(palettes.AROYA),
  },
  {
    get(target, prop) {
      if (prop in target) return target[prop]
      if (!IGNORED.has(prop)) logger.warn('requested theme', prop, 'not found in themes', repr(target))
      return target.DEFAULT
    },
  },
)

const colorsByKeyThemeBuilder = shallowEqualsMemoizer((theme, colorsByKey) => {
  const { augmentColor } = theme.palette

  const newColors = Object.entries(colorsByKey).reduce((colors, [key, color]) => {
    if (key.match(/^[a-z]/) && color?.main) {
      colors[key] = augmentColor({ color, name: key })
      if (key.toUpperCase() in colorsByKey) {
        colors[key.toUpperCase()] = colors[key]
      }
    }
    return colors
  }, {})

  const newTheme = buildTheme(newColors)

  const palette = new Proxy(newTheme.palette, {
    get(target, prop) {
      if (typeof prop !== 'string' || prop === 'vars' || prop === 'isLuxonDateTime') return undefined
      if (prop in target) return target[prop]
      if (prop.match(/inherit/i)) return prop
      return undefined
    },
  })

  const finalTheme = {
    ...newTheme,
    palette,
    vars: {
      ...newTheme.vars,
      palette: new Proxy(newTheme.vars.palette, {
        get(target, prop) {
          if (typeof prop !== 'string' || prop === 'vars' || prop === 'isLuxonDateTime' || prop === '$$typeof') {
            return undefined
          }
          if (prop in target) return target[prop]
          if (prop.match(/inherit/i)) return prop
          return prop in palette ? palette[prop] : undefined
        },
      }),
    },
  }
  return finalTheme
})
/**
 * @param {Object} props
 * @param {import('react').ReactNode} props.children
 * @param {Object} [props.colorMapOverride]
 * @returns {import('react').ReactNode}
 */
export const ColorsByKeyThemeProvider = ({ children, colorMapOverride }) => {
  const { colorsByKey, dataTypes } = useConnectModern('selectColorsByKey', 'selectDataTypes')
  const newColors = useMemo(() => {
    const dataTypeColors = Object.values(dataTypes ?? {}).reduce((colors, dataType) => {
      if (dataType?.key && dataType?.color) {
        colors[dataType.key] = { main: dataType.color }
      }
      return colors
    }, {})

    return {
      ...dataTypeColors,
      ...colorsByKey,
      ...colorMapOverride,
    }
  }, [colorMapOverride, colorsByKey, dataTypes])

  return <ThemeProvider theme={theme => colorsByKeyThemeBuilder(theme, newColors)}>{children}</ThemeProvider>
}

export default themes
if (
  (safeStorage('local').getItem('debug') === 'true' && appConfig.ENVIRONMENT !== 'production') ||
  (typeof process !== 'undefined' && process.env.NODE_ENV !== 'test')
) {
  getGlobal().themes = themes
}
